import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import { RMSCommonDialog, RMSConfirmationDialog } from 'features/shared/components/dialog';
import { Typography } from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import { RMSMenuSidebar } from 'features/shared/components/sidebars';
import PersonHeader from 'features/user/components/add-edit-person/PersonHeader';
import {
  getSelectedPersonMenu,
  PersonSideBarMenuItem,
  GRID_MENU_KEYS,
} from 'features/user/constants/PersonSideBarMenuHelper';
import { PersonMenuItemKey } from 'features/user/constants/personMenuItemKeys';
import { RMSButton } from 'features/shared/components/buttons';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { showAddWarrant, setNewCreatedPesonAsSelected } from 'reducers/PersonDialogsReducer';
import { usePersonDialog } from 'features/user/hooks/usePersonDialog';
import { personSaved } from 'features/user/store/actions/personDialog.actions';

const AddEditPersonDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const personFormRef = useRef(null);
  const photoTabRef = useRef(null);
  const { personId, requestId, closeOnSave, origin, initialMenuKey } = dialog;
  const { closePersonDialog } = usePersonDialog();

  const [activePersonId, setActivePersonId] = useState(personId || null);
  const [headerRefreshKey, setHeaderRefreshKey] = useState(0);
  const isEditMode = Boolean(activePersonId);
  const [loading, setLoading] = useState(false);
  const [personFormState, setPersonFormState] = useState({
    isDirty: false,
    canSave: true,
    submitting: false,
    recordCreated: null,
    recordCreatedBy: null,
    recordUpdated: null,
    recordUpdatedBy: null,
  });

  const [activeMenuKey, setActiveMenuKey] = useState(PersonMenuItemKey.PERSON_DETAILS);
  const [autoOpenTag, setAutoOpenTag] = useState(false);

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const selectedMenu = getSelectedPersonMenu(activeMenuKey);
  const showGridTitle = GRID_MENU_KEYS.has(activeMenuKey);

  const ActiveComponent = selectedMenu?.content;

  const isActiveTabDirty = useCallback(
    () =>
      (activeMenuKey === PersonMenuItemKey.PERSON_DETAILS && personFormState.isDirty) ||
      (activeMenuKey === PersonMenuItemKey.PHOTO && photoTabRef.current?.isDirty?.()),
    [activeMenuKey, personFormState.isDirty]
  );

  const handleClose = useCallback(() => {
    const close = () => {
      if (origin === 'party' && activePersonId) {
        dispatch(setNewCreatedPesonAsSelected(activePersonId));
      }
      closePersonDialog(requestId);
    };
    if (isActiveTabDirty()) {
      requestConfirm(
        'You have unsaved changes. Are you sure you want to close without saving?',
        close,
        'Unsaved Changes'
      );
      return;
    }
    close();
  }, [
    origin,
    activePersonId,
    dispatch,
    closePersonDialog,
    requestId,
    isActiveTabDirty,
    requestConfirm,
  ]);

  const handleSave = () => personFormRef.current?.submit();
  const handlePersonFormStateChange = useCallback((state) => {
    setPersonFormState(state);
  }, []);

  const handlePersonSaved = useCallback(
    (person, { isEditMode }) => {
      if (person?.ptsPersonID) setActivePersonId(person.ptsPersonID);
      setHeaderRefreshKey((key) => key + 1);
      dispatch(personSaved(person, { isEditMode, requestId }));
      if (closeOnSave) closePersonDialog(requestId);
    },
    [dispatch, requestId, closeOnSave, closePersonDialog]
  );

  const handleTagsChanged = useCallback(() => {
    setHeaderRefreshKey((key) => key + 1);
  }, []);

  const handlePrimaryAddressChanged = useCallback(() => {
    setHeaderRefreshKey((key) => key + 1);
  }, []);

  const handleRelatedRecordsChanged = useCallback(() => {
    setHeaderRefreshKey((key) => key + 1);
  }, []);

  const handleMenuSelect = useCallback(
    (menuKey, options = {}) => {
      if (menuKey === activeMenuKey) {
        if (options.autoOpenTag && menuKey === PersonMenuItemKey.TAGS) setAutoOpenTag(true);
        return;
      }

      if (!activePersonId && menuKey !== PersonMenuItemKey.PERSON_DETAILS) {
        dispatch(notify('Create the person first to access the other tabs.', 'warning'));
        return;
      }

      const changeMenu = () => {
        setPersonFormState((state) => ({ ...state, isDirty: false }));
        setAutoOpenTag(Boolean(options.autoOpenTag && menuKey === PersonMenuItemKey.TAGS));
        setActiveMenuKey(menuKey);
      };

      if (isActiveTabDirty()) {
        requestConfirm(
          'You have unsaved changes. Are you sure you want to leave without saving?',
          changeMenu,
          'Unsaved Changes'
        );
        return;
      }

      changeMenu();
    },
    [activeMenuKey, activePersonId, isActiveTabDirty, requestConfirm, dispatch]
  );

  useEffect(() => {
    setActivePersonId(personId || null);
    setActiveMenuKey(
      personId
        ? initialMenuKey || PersonMenuItemKey.PERSON_DETAILS
        : PersonMenuItemKey.PERSON_DETAILS
    );
    setAutoOpenTag(false);
  }, [personId, initialMenuKey]);

  const handleAddTagFromHeader = useCallback(
    () => handleMenuSelect(PersonMenuItemKey.TAGS, { autoOpenTag: true }),
    [handleMenuSelect]
  );
  const handleAutoOpenTagHandled = useCallback(() => setAutoOpenTag(false), []);

  const handleNewWarrantClick = useCallback(() => {
    dispatch(showAddWarrant({ ptsPersonID: activePersonId }));
  }, [dispatch, activePersonId]);

  return (
    <div>
      <RMSCommonDialog
        fullScreen={true}
        className="add-person-dialog"
        dialogClassName="add-person-dialog-shell"
        open={true}
        saveButton={{
          show: activeMenuKey === PersonMenuItemKey.PERSON_DETAILS,
          type: 'simple',
          color: 'primary',
          label: 'Save',
        }}
        onSave={handleSave}
        loading={loading}
        saveDisabled={
          loading ||
          personFormState.submitting ||
          !personFormState.canSave ||
          !personFormState.isDirty
        }
        title={activePersonId ? 'Edit Person' : 'Add Person'}
        closeOnBackdropClick={false}
        maxWidth="md"
        onClose={handleClose}
        fullWidth
        leftActions={
          isEditMode && (
            <RMSButton
              label="History"
              variant="contained"
              color="primary"
              prefixIcon={<HistoryIcon />}
              disabled={!personFormState.recordCreated}
              onClick={() => handleMenuSelect(PersonMenuItemKey.HISTORY)}
            />
          )
        }>
        <div className="pd-page">
          {/* header */}
          <PersonHeader
            personId={activePersonId}
            refreshKey={headerRefreshKey}
            onMenuSelect={handleMenuSelect}
            onAddTagClick={handleAddTagFromHeader}
            onNewWarrantClick={isEditMode ? handleNewWarrantClick : undefined}
          />

          <div className="pd-body">
            <RMSMenuSidebar
              groups={PersonSideBarMenuItem}
              className="pd-sidebar"
              activeKey={activeMenuKey}
              onSelect={handleMenuSelect}
            />

            <main className="pd-content">
              {showGridTitle && (
                <Typography className="pd-content__title" variant="h6">
                  {selectedMenu?.label}
                </Typography>
              )}
              <div className={`pd-content__body${showGridTitle ? ' pd-content__body--grid' : ''}`}>
                {ActiveComponent ? (
                  activeMenuKey === PersonMenuItemKey.PERSON_DETAILS ? (
                    <ActiveComponent
                      ref={personFormRef}
                      personId={activePersonId}
                      onLoadingChange={setLoading}
                      onStateChange={handlePersonFormStateChange}
                      onSaved={handlePersonSaved}
                    />
                  ) : activeMenuKey === PersonMenuItemKey.TAGS ? (
                    <ActiveComponent
                      personId={activePersonId}
                      autoOpenAdd={autoOpenTag}
                      onAutoOpenAddHandled={handleAutoOpenTagHandled}
                      onTagsChanged={handleTagsChanged}
                    />
                  ) : activeMenuKey === PersonMenuItemKey.ADDRESSES ? (
                    <ActiveComponent
                      personId={activePersonId}
                      onPrimaryAddressChanged={handlePrimaryAddressChanged}
                    />
                  ) : activeMenuKey === PersonMenuItemKey.SOPS ? (
                    <ActiveComponent
                      personId={activePersonId}
                      onSopsChanged={handleRelatedRecordsChanged}
                    />
                  ) : activeMenuKey === PersonMenuItemKey.BULLETINS ? (
                    <ActiveComponent
                      personId={activePersonId}
                      onBulletinsChanged={handleRelatedRecordsChanged}
                    />
                  ) : activeMenuKey === PersonMenuItemKey.RELATED_RECORDS ? (
                    <ActiveComponent personId={activePersonId} onNavigateAway={handleClose} />
                  ) : activeMenuKey === PersonMenuItemKey.PHOTO ? (
                    <ActiveComponent ref={photoTabRef} personId={activePersonId} />
                  ) : (
                    <ActiveComponent personId={activePersonId} />
                  )
                ) : (
                  <Typography color="error">Tab not found</Typography>
                )}
              </div>
            </main>
          </div>
        </div>
      </RMSCommonDialog>
      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </div>
  );
};

export default AddEditPersonDialog;
